/*
 * A Goal Plan's cart lines (the real paid plan + its $0 fulfillment-only
 * recipe lines, all sharing the same backend_plan_id property) are added
 * together as one fixed set. If the customer removes just the paid plan
 * line — using the cart's own normal Remove button, not a plan-specific
 * one — the $0 recipe lines for that plan are left behind with nothing
 * paying for them. This watches every cart-mutating request the theme
 * makes, regardless of which button/component triggered it, and cleans
 * up any such orphaned recipe lines right after.
 */
(function () {
  if (window.__resetCartPlanGuardInstalled) return;
  window.__resetCartPlanGuardInstalled = true;

  // The theme's own cart component expects a much richer event than a
  // plain notification to actually refresh what's on screen — it wants a
  // promise resolving to freshly server-rendered HTML for its own section,
  // keyed by an internal section id. Rather than try to replicate that
  // exactly, just reload the page once real removals actually happened —
  // guarantees the customer always sees the true, correct cart, at the
  // small cost of a refresh instead of a silent live update.

  // backend_plan_id/backend_fulfillment_only may be hidden from the
  // customer with a leading underscore (Shopify's convention for that) —
  // check both forms so this keeps working whichever one a given line
  // actually has.
  function readPlanId(props) {
    return props.backend_plan_id || props._backend_plan_id;
  }
  function isFulfillmentOnly(props) {
    return props.backend_fulfillment_only === 'true' || props._backend_fulfillment_only === 'true';
  }

  // Finds every orphaned $0 component line in a freshly-fetched cart — any
  // whose backend_plan_id has no matching non-fulfillment-only (real paid
  // plan) line anywhere in the same cart. Returns line INDICES (0-based,
  // matching cart.items), not the 1-based line numbers /cart/change.js
  // takes — these get used to build one /cart/update.js quantities array
  // instead.
  function findOrphanLineIndices(cart) {
    var items = cart.items || [];
    var planIdsWithPaidLine = {};

    items.forEach(function (item) {
      var props = item.properties || {};
      var planId = readPlanId(props);
      if (planId && !isFulfillmentOnly(props)) {
        planIdsWithPaidLine[planId] = true;
      }
    });

    var orphanIndices = [];
    items.forEach(function (item, i) {
      var props = item.properties || {};
      var planId = readPlanId(props);
      if (planId && isFulfillmentOnly(props) && !planIdsWithPaidLine[planId]) {
        orphanIndices.push(i);
      }
    });
    return orphanIndices;
  }

  // Removing orphans one at a time (a fetch, then a /cart/change.js call,
  // repeated per line — up to 25 round trips for a full 6-day plan's worth
  // of $0 component lines) was the actual reason removing a plan from the
  // cart could take a very long time, confirmed live. A single
  // /cart/update.js call does the same cleanup in one round trip —
  // but keyed by each orphan line's own unique `key` (variant_id +
  // properties hash), NOT a positional quantities array matched by line
  // order/count. This was the actual cause of a customer's whole cart
  // silently emptying: the fetch above and the update below aren't atomic,
  // so if the cart's line order or count changed in that gap (e.g. another
  // item added right around the same moment, plausible right after
  // returning from checkout), a positional array built from the old
  // snapshot would get applied to the new cart's lines at the wrong
  // positions, zeroing out whatever happened to now sit there. Updating by
  // key only ever touches the exact lines identified as orphaned, so
  // nothing else in the cart can be affected no matter what else changed
  // in between.
  var cleaningUntil = 0;
  function cleanupOrphanedPlanComponents() {
    if (Date.now() < cleaningUntil) return;
    cleaningUntil = Date.now() + 8000;

    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        var orphanIndices = findOrphanLineIndices(cart);
        if (!orphanIndices.length) return null;

        var updates = {};
        orphanIndices.forEach(function (i) {
          updates[items[i].key] = 0;
        });

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function (res) { return res.json(); });
      })
      .then(function (result) {
        if (result) window.location.reload();
      })
      .catch(function () {})
      .then(function () {
        cleaningUntil = 0;
      });
  }

  // Two different event-detection approaches (watching specific network
  // calls, then watching clicks on the Remove button) both failed to catch
  // every case reliably — this theme's cart is built from newer web
  // components whose exact internals aren't predictable from the outside.
  // Polling sidesteps that entirely: it doesn't need to know what triggered
  // a removal, just checks the real cart state on a steady interval and
  // fixes it if something's wrong. cleanupOrphanedPlanComponents() is a
  // cheap no-op read when there's nothing to clean up.
  setInterval(cleanupOrphanedPlanComponents, 2000);
  cleanupOrphanedPlanComponents();
})();
